import React from 'react';
import { DocumentCheckIcon } from '@heroicons/react/24/outline';

const TermsOfService = () => {
  return (
    <div className="font-sans text-slate-900 bg-white min-h-screen">

      {/* Nav */}
      <nav className="fixed w-full z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center gap-2.5">
            <a href="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-sky-200">
                <DocumentCheckIcon className="h-4 w-4" />
              </div>
              <span className="font-extrabold text-lg tracking-tight text-slate-900">ImmiSpace</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <h1 className="text-4xl font-black text-slate-900 mb-12">Terms of Service</h1>

        <div className="prose prose-slate max-w-none space-y-10 text-slate-600 leading-relaxed">

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Introduction</h2>
            <p>
              This User Agreement ("Agreement") is made between DOCUP ("Company") and the user ("User") who creates an account on our application ("Application"). By creating an account and using our Application, the User agrees to the terms and conditions set forth in this Agreement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">1. Collection and Use of Data</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">1.1 Collected Data</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Personal Information:</strong> Includes name, email address, contact details, and any other information provided by the User when creating the account or using the Application.</li>
              <li><strong>Client Information:</strong> Includes documents and data submitted by the User's clients as part of the services provided through the Application.</li>
              <li><strong>Usage Data:</strong> Includes information about how the User interacts with the Application, including IP addresses, browser types, and access times.</li>
            </ul>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">1.2 Use of Data</h3>
            <p className="mb-2">We use your data to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Provide, operate, and maintain our Application.</li>
              <li>Improve, personalize, and develop our Application.</li>
              <li>Understand and analyze how the User uses our Application.</li>
              <li>Develop new products, services, features, and functionalities.</li>
              <li>Communicate with the User, including for customer service, updates, and other information related to the Application.</li>
              <li>Process transactions and manage subscriptions.</li>
              <li>Detect and prevent fraud and other illegal activities.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">2. Limitations on Data Use</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">2.1 Prohibited Uses</h3>
            <p className="mb-2">We will never use your data to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Engage in business activities related to the User's industry, including immigration services.</li>
              <li>Contact the User's clients except for technical support requests made by the User.</li>
              <li>Sell or rent personal data to third parties.</li>
              <li>Share personal data with third parties other than those listed in section 4.1.</li>
              <li>Use the data for purposes other than those specified in this Agreement without the User's explicit consent.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">3. Data Protection and Security</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">3.1 Data Security</h3>
            <p>We implement industry-standard measures, including encryption and access controls, to ensure a level of security appropriate to the risk of processing personal data.</p>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">3.2 Data Retention</h3>
            <p>We will retain personal data only as long as necessary to achieve the purposes for which it was collected. Users can delete their accounts at any time, which will result in complete deletion of their data from our servers.</p>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">3.3 Location of Data</h3>
            <p>All data is stored in North America with a host that complies with industry standards (e.g., FedRAMP, EU Data Directive, FISMA).</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">4. Sharing and Disclosure of Data</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">4.1 Third-Party Service Providers</h3>
            <p>We may share data with third-party service providers who perform services on our behalf, such as payment processing, data analysis, and email delivery. Providers must maintain confidentiality and use data only for its intended purpose.</p>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">4.2 Legal Requirements</h3>
            <p>We may disclose data if required by law or in response to legitimate requests from public authorities.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">5. User Responsibilities</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">5.1 Account Security</h3>
            <p>The User is responsible for maintaining the confidentiality of their account credentials and for all activities under their account, including actions of collaborators granted access.</p>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">5.2 Compliance with Laws</h3>
            <p>The User is responsible for complying with all applicable laws regarding collection and processing of data.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">6. Termination</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">6.1 Termination by the User</h3>
            <p>The User may terminate this Agreement at any time by closing their account. All data will be deleted upon account termination.</p>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">6.2 Termination by the Company</h3>
            <p>The Company may terminate this Agreement and the User's access at any time, with or without cause and notice.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">7. Governing Law and Arbitration</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">7.1 Governing Law</h3>
            <p>This Agreement and any disputes shall be governed by the laws of the Province of Quebec, Canada.</p>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">7.2 Arbitration</h3>
            <p>Disputes will be resolved exclusively by binding arbitration administered by a recognized arbitration body (e.g., ADR Institute of Canada) in Montreal, Quebec, Canada.</p>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">7.3 Scope of Arbitration</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Single arbitrator with expertise in commercial and technology contracts.</li>
              <li>Decision is final and binding; judgment may be entered in any competent court.</li>
              <li>Arbitration shall be conducted in English.</li>
            </ul>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">7.4 Limitations on Litigation</h3>
            <p>Parties waive the right to bring lawsuits regarding this Agreement in any court other than arbitration. Nothing prevents seeking injunctive or equitable relief to protect IP or confidential information.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">8. Warranty and Limitation of Liability</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">8.1 Limitation of Liability</h3>
            <p className="mb-2">DocUp and its suppliers, officers, affiliates, representatives, contractors, and employees shall not be liable for any subject matter of this Agreement under any contract, negligence, strict liability, or other theory:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Errors, interruptions, or loss/corruption of data, cost of substitute goods/services/technology, or loss of business;</li>
              <li>Indirect, exemplary, incidental, special, or consequential damages;</li>
              <li>Matters beyond DocUp's reasonable control;</li>
              <li>Amounts exceeding fees paid by the Customer for the Services in the 12 months prior to the act giving rise to liability, whether or not DocUp was advised of the possibility of such damages.</li>
            </ul>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">8.2 Warranty</h3>
            <p>DocUp uses reasonable efforts to maintain Services with minimal errors and interruptions. Services may be temporarily unavailable for maintenance. Services are provided "as is"; DocUp disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">9. Data Security, Third-Party Services, and Collaboration</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">9.1 Third-Party Services</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>DocUp may use third-party services to provide, process, or enhance the Application.</li>
              <li>DocUp is not responsible for loss, delay, error, or disruption caused by third-party services.</li>
              <li>Users acknowledge reliance on third-party services is at their own risk.</li>
            </ul>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">9.2 Cybersecurity and Data Responsibility</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Users are responsible for the nature, legality, and sensitivity of uploaded/shared documents, including personal IDs, medical records, financial documents, or other regulated/confidential information.</li>
              <li>DocUp does not monitor or control the type, content, or legality of uploaded documents.</li>
              <li>DocUp implements industry-standard measures such as encryption and access controls but cannot guarantee against unauthorized access, disclosure, or cyberattacks.</li>
              <li>Users shall indemnify and hold harmless DocUp from claims, losses, damages, or liabilities arising from uploaded documents or their use.</li>
            </ul>
            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">9.3 Collaboration and Shared Accounts</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Users are responsible for all actions under their account, including actions of collaborators granted access.</li>
              <li>DocUp is not liable for actions performed by collaborators or team members sharing account access.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">10. Ownership of Content</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>All uploaded documents, files, or information remain the property of the user or entity that owns the content.</li>
              <li>DocUp does not claim ownership over user content.</li>
              <li>By uploading content, the user grants DocUp a non-exclusive, worldwide, royalty-free license to store, process, and display the content solely to provide the Services.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">11. Changing Fees</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>DocUp may offer certain Services for free with limits (e.g., storage, users, features).</li>
              <li>DocUp may modify, introduce, or discontinue fees, plans, or subscriptions at any time.</li>
              <li>Users will be notified at least 15 days in advance of pricing changes.</li>
              <li>Continued use after a pricing change constitutes acceptance. Users may cancel or downgrade before new fees take effect.</li>
              <li>Failure to pay may result in suspension or termination of paid services; users retain ownership of uploaded content.</li>
            </ul>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-100 py-8 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} ImmiSpace. All rights reserved.
      </div>

    </div>
  );
};

export default TermsOfService;
